import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

interface SidebarProps {
  activeMenu: string;
  onMenuChange: (menu: string) => void;
  collapsed: boolean;
  onToggle: () => void;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  /** Allowed roles. Empty/missing = all authenticated roles. */
  roles: string[];
  /** Optional UserPermissions flag. If set, the item is hidden when
   *  user.module_permissions[<key>] is false (admins are exempt — the
   *  backend always returns all-true for admin role). */
  permission?:
    | 'can_view_dashboard' | 'can_view_inbound' | 'can_view_outbound'
    | 'can_view_inventory' | 'can_view_transport' | 'can_use_halley'
    | 'can_manage_users' | 'can_view_expiry';
  section?: string;
}

// Convenience role groups — every operational menu item used to list
// `[admin, supervisor, customer]` only, which silently locked
// warehouse_manager / manager / project_leader / inbound_team /
// outbound_team out of Inventory, Inbound, Outbound, etc. (a
// warehouse_manager named JUB ended up seeing only Alert Rules,
// Teams, and Team Chat). The new groups make the role list explicit
// and reusable.
const HALA_STAFF = [
  'admin', 'supervisor', 'manager', 'warehouse_manager',
  'project_leader', 'inbound_team', 'outbound_team',
];
const ALL_AUTH = [...HALA_STAFF, 'customer'];

const items: MenuItem[] = [
  { id: 'ai-home', label: 'Halley AI', roles: ALL_AUTH, permission: 'can_use_halley' },
  { id: 'dashboard', label: 'Dashboard', roles: ALL_AUTH, permission: 'can_view_dashboard' },
  { id: 'separator-1', label: '', roles: ALL_AUTH, section: 'sep' },
  { id: 'inventory', label: 'Inventory', roles: ALL_AUTH, permission: 'can_view_inventory' },
  { id: 'inbound', label: 'Inbound', roles: ALL_AUTH, permission: 'can_view_inbound' },
  { id: 'outbound', label: 'Outbound', roles: ALL_AUTH, permission: 'can_view_outbound' },
  { id: 'transport', label: 'Transport', roles: ALL_AUTH, permission: 'can_view_transport' },
  { id: 'bookings', label: 'Bookings', roles: ALL_AUTH, permission: 'can_view_inbound' },
  { id: 'separator-2', label: '', roles: ALL_AUTH, section: 'sep' },
  { id: 'documents', label: 'Documents', roles: ALL_AUTH },
  { id: 'appointments', label: 'Appointments', roles: ALL_AUTH },
  { id: 'expiry', label: 'Expiry Tracker', roles: ALL_AUTH, permission: 'can_view_expiry' },
  { id: 'separator-3', label: '', roles: HALA_STAFF, section: 'sep' },
  // 'alarms' menu entry removed (May 17, 2026) — AlarmsPage was retired
  // months ago and only renders a "moved to Alert Rules" deprecation
  // notice. The route stays registered in App.tsx so any old bookmarks
  // still land on a useful redirect page, but new users shouldn't see
  // it in the navigation.
  { id: 'alert-rules', label: 'Alert Rules', roles: ALL_AUTH },
  { id: 'reports', label: 'Reports', roles: ALL_AUTH },
  { id: 'satisfaction', label: 'Satisfaction', roles: ['admin', 'supervisor', 'customer'] },
  { id: 'project-leaders', label: 'Project Leaders', roles: ['admin', 'customer'] },
  { id: 'teams', label: 'Teams', roles: HALA_STAFF, permission: 'can_manage_users' },
  { id: 'separator-4', label: '', roles: ALL_AUTH, section: 'sep' },
  { id: 'chat', label: 'Team Chat', roles: ALL_AUTH },
  { id: 'announcements', label: 'Announcements', roles: ['admin', 'supervisor'] },
  { id: 'separator-5', label: '', roles: ['admin'], section: 'sep' },
  { id: 'approvals', label: 'Approvals', roles: ['admin'] },
  { id: 'customer-scope', label: 'Customer Scope', roles: ['admin'] },
  { id: 'unmapped-bmfirms', label: 'Unmapped BMFIRMs', roles: ['admin'] },
  { id: 'settings', label: 'Settings', roles: ['admin'] },
];

// Icon components
const IconSparkles = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.26 9a2 2 0 0 0 .94-3.97A1 1 0 0 0 9 5H5a1 1 0 0 0 0 2h.97a2 2 0 0 0 3.29 2Z" />
    <path d="M9 9v.01" />
    <path d="M12 8v.01" />
    <path d="M15 9a2 2 0 0 0 .97-3.97" />
    <path d="M6.5 13.5a2 2 0 0 0 2.12-3.12" />
    <path d="M17.5 13.5a2 2 0 0 0-2.12-3.12" />
  </svg>
);

const IconGrid = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x={3} y={3} width={7} height={7} />
    <rect x={14} y={3} width={7} height={7} />
    <rect x={14} y={14} width={7} height={7} />
    <rect x={3} y={14} width={7} height={7} />
  </svg>
);

const IconBox = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1={12} y1={22.08} x2={12} y2={12} />
  </svg>
);

const IconArrowDown = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <line x1={12} y1={5} x2={12} y2={19} />
    <polyline points="19 12 12 19 5 12" />
  </svg>
);

const IconArrowUp = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <line x1={12} y1={19} x2={12} y2={5} />
    <polyline points="5 12 12 5 19 12" />
  </svg>
);

const IconTruck = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x={1} y={6} width={5} height={10} />
    <path d="M6 6v10M6 16h13a2 2 0 0 0 2-2v-5a2 2 0 0 0-2-2h-3V6" />
    <circle cx={5.5} cy={19.5} r={2.5} />
    <circle cx={18.5} cy={19.5} r={2.5} />
  </svg>
);

const IconCalendarCheck = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x={2} y={4} width={20} height={18} rx={2} ry={2} />
    <line x1={16} y1={2} x2={16} y2={6} />
    <line x1={8} y1={2} x2={8} y2={6} />
    <line x1={2} y1={10} x2={22} y2={10} />
    <polyline points="15 13 10 18 7 15" />
  </svg>
);

const IconFileText = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1={10} y1={13} x2={14} y2={13} />
    <line x1={10} y1={17} x2={14} y2={17} />
  </svg>
);

const IconClock = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx={12} cy={12} r={10} />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconAlertTriangle = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3.05h16.94a2 2 0 0 0 1.71-3.05l-8.47-14.14a2 2 0 0 0-3.42 0z" />
    <line x1={12} y1={9} x2={12} y2={13} />
    <line x1={12} y1={17} x2={12.01} y2={17} />
  </svg>
);

const IconBell = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const IconBarChart = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <line x1={12} y1={20} x2={12} y2={10} />
    <line x1={18} y1={20} x2={18} y2={4} />
    <line x1={6} y1={20} x2={6} y2={16} />
  </svg>
);

const IconStar = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill={active ? theme.accent : 'none'} stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const IconUsers = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx={9} cy={7} r={4} />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconChat = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const IconMegaphone = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 11l18-5v12L3 13v-2z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </svg>
);

const IconShieldCheck = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const IconLayers = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </svg>
);

const IconLink = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const IconGear = ({ active, theme }: { active: boolean; theme: any }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={active ? theme.accent : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx={12} cy={12} r={3} />
    <path d="M12 1v6m0 6v6M4.22 4.22l4.24 4.24m2.12 2.12l4.24 4.24M1 12h6m6 0h6m-16.78 7.78l4.24-4.24m2.12-2.12l4.24-4.24" />
  </svg>
);

const IconDiamond = ({ theme }: { theme: any }) => (
  <img
    src="/hala-logo.png"
    alt="HALA"
    width={32}
    height={32}
    style={{
      objectFit: 'contain',
      filter: `drop-shadow(0 0 8px ${theme.accent}66)`,
    }}
  />
);

const getIconComponent = (id: string) => {
  switch (id) {
    case 'ai-home': return IconSparkles;
    case 'dashboard': return IconGrid;
    case 'inventory': return IconBox;
    case 'inbound': return IconArrowDown;
    case 'outbound': return IconArrowUp;
    case 'transport': return IconTruck;
    case 'bookings': return IconCalendarCheck;
    case 'documents': return IconFileText;
    case 'appointments': return IconClock;
    case 'expiry': return IconAlertTriangle;
    case 'alarms': return IconBell;
    case 'alert-rules': return IconBell;
    case 'reports': return IconBarChart;
    case 'satisfaction': return IconStar;
    case 'project-leaders': return IconUsers;
    case 'teams': return IconUsers;
    case 'chat': return IconChat;
    case 'announcements': return IconMegaphone;
    case 'approvals': return IconShieldCheck;
    case 'customer-scope': return IconLayers;
    case 'unmapped-bmfirms': return IconLink;
    case 'settings': return IconGear;
    default: return null;
  }
};

const Sidebar: React.FC<SidebarProps> = ({
  activeMenu, onMenuChange, collapsed, onToggle,
  isMobile = false, mobileOpen = false, onMobileClose,
}) => {
  const { user, logout } = useAuth();
  const { theme, bgModeName, setBgMode } = useTheme();
  const role = user?.role || 'customer';
  // Module permissions live on user.module_permissions (loaded by /auth/me).
  // Missing/undefined = admins-OK, non-admins-deny: that mirrors the backend
  // default-deny behaviour for sub-users where the admin hasn't ticked
  // anything yet.
  const perms = (user as any)?.module_permissions as Record<string, boolean> | undefined;
  const hasPermission = (key?: MenuItem['permission']) => {
    if (!key) return true;            // item isn't gated by a permission
    if (role === 'admin') return true; // admin sees everything
    if (!perms) return false;          // sub-user with no perms record
    return !!perms[key];
  };
  const filtered = items.filter(i => i.roles.includes(role) && hasPermission(i.permission));
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [showCollapseHint, setShowCollapseHint] = useState(false);
  const isDark = bgModeName === 'dark' || bgModeName === 'midnight';

  const toggleDarkMode = () => {
    setBgMode(isDark ? 'light' : 'dark');
  };

  // On mobile: overlay drawer; on desktop: fixed sidebar
  const showSidebar = isMobile ? mobileOpen : true;
  const sidebarWidth = isMobile ? 280 : (collapsed ? 80 : 240);
  const expanded = isMobile ? true : !collapsed; // always expanded text on mobile drawer

  const handleMenuClick = (id: string) => {
    onMenuChange(id);
    if (isMobile && onMobileClose) onMobileClose();
  };

  return (
    <>
      {/* Mobile backdrop overlay */}
      {isMobile && mobileOpen && (
        <div
          onClick={onMobileClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1099,
            transition: 'opacity 0.3s ease',
            backdropFilter: 'blur(4px)',
          }}
        />
      )}

      <div
        style={{
          width: sidebarWidth,
          // Sit 8px below the window edge so the sidebar doesn't run
          // flush against the chrome — visible as a small dark frame
          // around the app. On mobile we still anchor to the very top
          // so the drawer feels like a native sheet.
          height: isMobile ? '100vh' : 'calc(100vh - 8px)',
          background: 'linear-gradient(180deg, var(--card-bg) 0%, var(--bg-raised) 100%)',
          boxShadow: isMobile ? '4px 0 32px rgba(0,0,0,0.15)' : '4px 0 24px rgba(0,0,0,0.04)',
          display: 'flex',
          flexDirection: 'column',
          transition: isMobile
            ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'width 0.3s ease-out, box-shadow 0.3s ease',
          position: 'fixed',
          left: 0,
          top: isMobile ? 0 : 8,
          zIndex: 1100,
          overflow: 'hidden',
          transform: isMobile
            ? (mobileOpen ? 'translateX(0)' : 'translateX(-100%)')
            : 'translateX(0)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: (!isMobile && collapsed) ? '16px 0' : '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            justifyContent: (!isMobile && collapsed) ? 'center' : 'space-between',
            height: 72,
            borderBottom: '1px solid var(--border-subtle)',
            position: 'relative',
          }}
          onMouseEnter={() => setShowCollapseHint(!collapsed && !isMobile)}
          onMouseLeave={() => setShowCollapseHint(false)}
        >
          <div
            onClick={isMobile ? onMobileClose : onToggle}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'transform 0.2s ease',
            }}
            title={isMobile ? 'Close menu' : 'Toggle sidebar'}
          >
            <IconDiamond theme={theme} />
          </div>

          {expanded && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
              <div style={{ color: 'var(--text)', fontSize: 15, fontWeight: 700, letterSpacing: -0.5, lineHeight: 1 }}>
                HALA
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: 11, fontWeight: 500, letterSpacing: 0.5, lineHeight: 1 }}>
                WMS
              </div>
            </div>
          )}

          {/* Close button on mobile */}
          {isMobile && (
            <button
              onClick={onMobileClose}
              style={{
                width: 32, height: 32, borderRadius: 8,
                background: 'var(--bg-hover)', border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-secondary)', cursor: 'pointer', padding: 0,
              }}
            >
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <line x1={18} y1={6} x2={6} y2={18} />
                <line x1={6} y1={6} x2={18} y2={18} />
              </svg>
            </button>
          )}

          {!isMobile && !collapsed && showCollapseHint && (
            <button
              onClick={onToggle}
              style={{
                width: 24, height: 24, borderRadius: 4,
                background: 'transparent', border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-muted)', cursor: 'pointer', padding: 0,
                transition: 'color 0.2s',
              }}
              title="Collapse sidebar"
            >
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
        </div>

        {/* Nav */}
        <nav
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '12px 8px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {filtered.map(item => {
            if (item.section === 'sep') {
              return (
                <div
                  key={item.id}
                  style={{
                    height: 1,
                    background: 'linear-gradient(90deg, transparent, var(--border), transparent)',
                    margin: '8px 0',
                  }}
                />
              );
            }

            const active = activeMenu === item.id;
            const hovered = hoveredItem === item.id;
            const IconComponent = getIconComponent(item.id);

            return (
              <button
                key={item.id}
                onClick={() => handleMenuClick(item.id)}
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
                title={(!isMobile && collapsed) ? item.label : undefined}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: (!isMobile && collapsed) ? 'center' : 'flex-start',
                  gap: (!isMobile && collapsed) ? 0 : 12,
                  padding: (!isMobile && collapsed) ? '10px 0' : (isMobile ? '12px 14px' : '10px 14px'),
                  borderRadius: 8,
                  background: active ? `${theme.accent}0c` : hovered ? 'rgba(0,0,0,0.02)' : 'transparent',
                  color: active ? theme.accent : 'var(--text-secondary)',
                  fontSize: isMobile ? 14 : 13,
                  fontWeight: active ? 600 : 500,
                  border: 'none',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.2s ease-out',
                  borderLeft: active ? `3px solid ${theme.accent}` : '3px solid transparent',
                  paddingLeft: (!isMobile && collapsed) ? undefined : 'calc(14px - 3px)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: active ? theme.accent : 'var(--text-secondary)',
                    transition: 'color 0.2s',
                    flexShrink: 0,
                  }}
                >
                  {IconComponent && <IconComponent active={active} theme={theme} />}
                </div>

                {expanded && (
                  <span
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      transition: 'color 0.2s',
                    }}
                  >
                    {item.label}
                  </span>
                )}

                {!isMobile && collapsed && active && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 6,
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: theme.accent,
                    }}
                  />
                )}
              </button>
            );
          })}
        </nav>

        {/* Dark Mode Toggle + Search Shortcut */}
        <div style={{
          padding: (!isMobile && collapsed) ? '8px' : '8px 16px',
          display: 'flex',
          flexDirection: (!isMobile && collapsed) ? 'column' : 'row',
          alignItems: 'center',
          gap: 8,
          borderTop: '1px solid var(--border-subtle)',
        }}>
          {/* Dark Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            style={{
              width: (!isMobile && collapsed) ? 36 : 36,
              height: 36,
              borderRadius: 10,
              background: isDark ? 'rgba(250,204,21,0.12)' : 'rgba(99,102,241,0.08)',
              border: `1px solid ${isDark ? 'rgba(250,204,21,0.25)' : 'var(--border)'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              flexShrink: 0,
            }}
          >
            {isDark ? (
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#facc15" strokeWidth={2} strokeLinecap="round">
                <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            ) : (
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth={2} strokeLinecap="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </button>

          {/* Search Shortcut Button (only when expanded) */}
          {expanded && (
            <button
              onClick={() => {
                // Dispatch Ctrl+K event to trigger global search
                window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }));
              }}
              style={{
                flex: 1,
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', borderRadius: 10,
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                color: 'var(--text-muted)',
                fontSize: 12, cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = theme.accent; e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-surface)'; }}
            >
              <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <span style={{ flex: 1, textAlign: 'left' }}>Search</span>
              <kbd style={{
                fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                background: 'var(--card-bg)', border: '1px solid var(--border)',
                color: 'var(--text-muted)', fontFamily: 'inherit',
              }}>⌘K</kbd>
            </button>
          )}
        </div>

        {/* Footer */}
        {user && (
          <div
            style={{
              padding: (!isMobile && collapsed) ? '12px 8px' : '16px 16px',
              borderTop: '1px solid var(--border-subtle)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: (!isMobile && collapsed) ? 'center' : 'space-between',
              gap: (!isMobile && collapsed) ? 0 : 10,
              position: 'relative',
            }}
          >
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div
                style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentHover})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 13, fontWeight: 700,
                  boxShadow: `0 2px 8px ${theme.accent}20`,
                }}
              >
                {user.display_name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div
                style={{
                  position: 'absolute', bottom: 0, right: 0,
                  width: 8, height: 8, borderRadius: '50%',
                  background: '#10b981',
                  border: '2px solid var(--card-bg)',
                  boxShadow: '0 0 4px rgba(16, 185, 129, 0.5)',
                }}
              />
            </div>

            {expanded && (
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div
                  style={{
                    color: 'var(--text)', fontSize: 13, fontWeight: 600,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2,
                  }}
                >
                  {user.display_name}
                </div>
                <div
                  style={{
                    color: 'var(--text-muted)', fontSize: 11,
                    textTransform: 'capitalize', fontWeight: 500, lineHeight: 1,
                  }}
                >
                  {user.role}
                </div>
              </div>
            )}

            {expanded && (
              <button
                onClick={logout}
                style={{
                  width: 32, height: 32, borderRadius: 6,
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text-muted)', cursor: 'pointer',
                  transition: 'all 0.2s ease', flexShrink: 0, padding: 0,
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLButtonElement;
                  el.style.color = '#ef4444';
                  el.style.borderColor = '#ef4444';
                  el.style.background = '#ef444410';
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLButtonElement;
                  el.style.color = 'var(--text-muted)';
                  el.style.borderColor = 'var(--border)';
                  el.style.background = 'transparent';
                }}
                title="Sign out"
              >
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;
